import React from 'react';
import { Card, CardContent } from '@/components/ui/card';

const AboutTalent = () => {
  return (
    <div className="container mx-auto px-4 py-12" data-testid="gotalent-about-page">
      <div className="max-w-4xl mx-auto space-y-12">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">About & Trust GoTalent</h1>
          <p className="text-lg text-muted-foreground">
            GoTalent helps employers discover candidates with confidence — with clear profiles, transparent skills,
            and safer hiring workflows. Candidates can showcase their experience and control what they share.
          </p>
        </div>

        <Card>
          <CardContent className="p-8 space-y-8">
            <section className="space-y-3">
              <h2 className="text-2xl font-bold">What is GoTalent?</h2>
              <p className="text-muted-foreground leading-relaxed">
                GoTalent is GoFella’s talent marketplace where candidates create profiles to showcase skills, work history,
                and portfolios, while employers can search, shortlist, and connect through the portal. We focus on clarity,
                safer communication, and a respectful recruitment experience for both sides.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold">Trust & Verification</h2>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="text-3xl">🪪</div>
                  <div>
                    <h3 className="font-semibold mb-1">Candidate identity checks (optional)</h3>
                    <p className="text-sm text-muted-foreground">
                      Where enabled, candidates can complete extra checks to increase trust. Not all candidates will have
                      verification badges.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-3xl">🏢</div>
                  <div>
                    <h3 className="font-semibold mb-1">Employer verification (ABN)</h3>
                    <p className="text-sm text-muted-foreground">
                      Employers can display a verified ABN badge to help candidates feel safer when engaging with offers
                      and interview invitations.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-3xl">🔒</div>
                  <div>
                    <h3 className="font-semibold mb-1">Privacy-first profiles</h3>
                    <p className="text-sm text-muted-foreground">
                      Candidates can decide what to publish publicly. Sensitive details should only be shared when the
                      candidate is comfortable and the employer is legitimate.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-3xl">💬</div>
                  <div>
                    <h3 className="font-semibold mb-1">Safer messaging & requests</h3>
                    <p className="text-sm text-muted-foreground">
                      Communication starts on GoFella. Employers can send messages, offers, or interview invitations through
                      the portal, helping keep a record of conversations and reducing off-platform risks.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-3xl">🧾</div>
                  <div>
                    <h3 className="font-semibold mb-1">Clear expectations</h3>
                    <p className="text-sm text-muted-foreground">
                      Profiles and requests should be specific (role scope, work type, location, start date, and compensation
                      format) so both sides can make faster decisions.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold">For Candidates</h2>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Create a strong profile with skills, experience, and a portfolio (where available).</li>
                <li>Keep your availability and preferred work type up to date (full-time, part-time, contract).</li>
                <li>Be cautious with personal details — share sensitive information only when needed and appropriate.</li>
                <li>Review employer verification badges when available.</li>
                <li>Report suspicious messages, unrealistic offers, or pressure to move off-platform.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold">For Employers</h2>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Use the portal to search, shortlist, and contact candidates professionally.</li>
                <li>Provide clear role scope, work type, location expectations, and compensation format.</li>
                <li>Respect candidate privacy — request only what is necessary for hiring decisions.</li>
                <li>Keep communications respectful and avoid spammy outreach.</li>
                <li>Use interview invites and offers responsibly, and follow fair hiring practices.</li>
              </ul>
            </section>

            <section className="rounded-lg border bg-muted/30 p-5 space-y-2">
              <h2 className="text-lg font-semibold">Need help?</h2>
              <p className="text-sm text-muted-foreground">
                If you believe a profile, message, or employer request is unsafe or misleading, please report it. Our team
                may remove content, restrict accounts, or request additional verification when needed.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutTalent;
